if (!customElements.get('product-form')) {
  // Older Safari/Android (before 2022) lack crypto.randomUUID; build a v4 UUID from getRandomValues.
  const randomId = () => {
    if (crypto.randomUUID) return crypto.randomUUID();
    const b = crypto.getRandomValues(new Uint8Array(16));
    b[6] = (b[6] & 15) | 64;
    b[8] = (b[8] & 63) | 128;
    const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
    return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
  };
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();
        this.error = false;
        this.cartErrorMessage = '';
        this.cartSaved = false;
        this.checkoutRedirected = false;

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        document.querySelectorAll('cup-3d-configurator [name^="properties["]').forEach((control) => {
          if (!control.name || control.disabled) return;
          if (control.type === 'file') {
            const file = control.files?.[0];
            if (file) formData.set(control.name, file, file.name);
            return;
          }
          if ((control.type === 'checkbox' || control.type === 'radio') && !control.checked) return;
          if (control.value !== '') formData.set(control.name, control.value);
        });
        if (this.form.cupPreviewFile instanceof File) {
          formData.set(
            'properties[_Personalisierte Vorschau]',
            this.form.cupPreviewFile,
            this.form.cupPreviewFile.name
          );
        }
        if (this.form.cupStickerPrintFile instanceof File) {
          formData.set(
            'properties[_Druckvorlage]',
            this.form.cupStickerPrintFile,
            this.form.cupStickerPrintFile.name
          );
        }
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const cupSet = this.form.cupSetPayload;
        try {
         if (cupSet) {
          const manifest = cupSet.manifest;
          if (manifest.total !== manifest.designs.reduce((n, d) => n + d.count, 0)) throw new Error('Bitte die Motiv-Anzahlen prüfen.');
          formData.set('properties[_CupSet]', JSON.stringify(manifest));
          formData.set('properties[Motivaufteilung]', cupSet.summary);
          const attach = (key, value) => {
            if (value instanceof File) formData.set('properties[' + key + ']', value, value.name);
            else if (typeof value === 'string' && value.startsWith('https://cdn.shopify.com/')) formData.set('properties[' + key + ']', value);
            else throw new Error('Eine Motiv-Datei fehlt. Bitte das Set erneut öffnen.');
          };
          // Sets carry every motif inside the packages; the single-cup file fields would only add upload weight.
          formData.delete('properties[_Druckvorlage]');
          formData.delete('properties[_Quelldatei]');
          if (!cupSet.files.length || cupSet.files.some(([, value]) => !(value instanceof File))) throw new Error('Eine Motiv-Datei fehlt. Bitte das Set erneut öffnen.');
          attach('_Personalisierte Vorschau', cupSet.primaryPreview);
          // Every motif (text, colour, photo) lives in _CupSet; repeating it as visible properties made
          // cart, checkout and order mails very long. Visible stays only the short Motivaufteilung.
          for (const key of ['Datei', 'Text', 'Songtitel', 'Interpret', 'Bildbeschreibung', 'Hashtags', 'Social-Media-Stil', 'Sticker-Hintergrundfarbe']) {
            formData.delete('properties[' + key + ']');
          }
         }
        } catch (error) {
          this.error = true;
          this.cartErrorMessage = error.message || 'Dein Set konnte nicht vorbereitet werden. Bitte prüfe die Motive.';
          this.handleErrorMessage(this.cartErrorMessage);
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
          this.querySelector('.loading__spinner').classList.add('hidden');
          this.form.dispatchEvent(new CustomEvent('product-form:complete', { bubbles: true, detail: { error: true, message: this.cartErrorMessage } }));
          return;
        }

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const redirectToCheckout = this.form.dataset.redirectCheckout === 'true';
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        this.submitCart(config, formData)
          .then(async (response) => {
            if (!response.status) this.cartSaved = true;
            if (response.status) {
              this.error = true;
              this.cartErrorMessage = response.description || response.message || 'Der Artikel konnte nicht in den Warenkorb gelegt werden.';
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(this.cartErrorMessage);
              this.dispatchCartErrorEvent(this.cartErrorMessage, 'INVALID');
              linesUpdateDeferred?.reject(new Error(this.cartErrorMessage));
              return;
            } else if (redirectToCheckout) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              try {
                await window.theCupLabSyncPreviews?.();
              } catch {
                this.cartErrorMessage = this.cartMessage('previewSyncError');
                window.alert(this.cartErrorMessage);
                window.location.assign(window.routes.cart_url);
                return;
              }
              this.checkoutRedirected = true;
              window.location.assign('/checkout');
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.error = true;
            this.cartErrorMessage = e instanceof TypeError ? this.cartMessage('networkError') : e.message || this.cartMessage('invalidResponse');
            this.handleErrorMessage(this.cartErrorMessage);
            this.dispatchCartErrorEvent(this.cartErrorMessage, 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            if (this.error) {
              const wasPending = this.cart?.addPending;
              this.cart?.finishAdd?.();
              if (wasPending) this.cart?.close?.();
            }
            this.submitButton.classList.remove('loading');
            if (!this.error && this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');
            this.form.dispatchEvent(new CustomEvent('product-form:complete', {
              bubbles: true,
              detail: { error: Boolean(this.error), added: Boolean(this.cartSaved), checkoutRedirected: Boolean(this.checkoutRedirected), message: this.cartErrorMessage },
            }));

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      async submitCart(config, formData) {
        const editing = this.form.cupEditItem;
        const cupFiles = this.form.cupSetPayload?.files;
        if (!editing) {
          if (cupFiles) return this.uploadCupSet(config, formData, cupFiles, true);
          return this.addWithProgress(routes.cart_add_url, config, formData);
        }
        const root = window.Shopify?.routes?.root || '/';
        const readCart = async () => {
          const response = await fetch(`${root}cart.js`, { credentials: 'same-origin' });
          const cart = await this.parseCartResponse(response, true);
          if (cart.status) throw new Error(cart.description);
          return cart;
        };
        const originalCart = await readCart();
        const original = originalCart.items.find((item) => item.key === editing.key);
        if (!original || original.quantity !== editing.quantity) throw new Error('Das Set wurde zwischenzeitlich geändert. Bitte öffne es erneut aus dem Warenkorb.');
        const token = randomId();
        formData.set('properties[_cup_edit_token]', token);
        formData.set('quantity', String(editing.quantity));
        const added = cupFiles
          ? await this.uploadCupSet(config, formData, cupFiles, false)
          : await this.addWithProgress(routes.cart_add_url, config, formData);
        if (added.status) return added;
        let cart = await readCart();
        const replacement = cart.items.find((item) => item.properties?.['_cup_edit_token'] === token);
        if (!replacement) throw new Error('Die Änderung konnte nicht sicher zugeordnet werden. Bitte prüfe den Warenkorb vor dem Bezahlen.');
        const removeLine = (key, sections = false) => fetch(`${root}cart/change.js`, {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ id: key, quantity: 0, ...(sections ? {
            sections: this.cart?.getSectionsToRender().map((section) => section.id) || [],
            sections_url: window.location.pathname
          } : {}) })
        });
        try {
          const changedResponse = await removeLine(editing.key, true);
          const changed = await this.parseCartResponse(changedResponse, true);
          if (!changedResponse.ok || changed.status) throw new Error('Das bisherige Set konnte nicht ersetzt werden.');
          this.form.cupEditItem = { key: replacement.key, quantity: editing.quantity };
          const updatedUrl = new URL(window.location.href);
          updatedUrl.searchParams.set('cup_edit', replacement.key);
          history.replaceState(null, '', updatedUrl);
          return { ...changed, id: replacement.id };
        } catch (error) {
          cart = await readCart();
          if (cart.items.some((item) => item.key === editing.key)) {
            const rollback = await removeLine(replacement.key);
            const rolledBack = await this.parseCartResponse(rollback, true);
            if (!rollback.ok || rolledBack.status) throw new Error(this.cartMessage('invalidResponse'));
            throw error;
          }
          delete this.form.cupEditItem;
          window.location.assign(routes.cart_url);
          throw new Error('Die Änderung wurde gespeichert. Der Warenkorb wird neu geladen.');
        }
      }

      // One motif ("Alle Cups gleich"): still one request, but always with visible upload progress in the cart
      // drawer and the configurator (Kani 18.10.2026: every upload shows its progress, on phones and desktops).
      async addWithProgress(url, config, formData) {
        const entries = [...formData.entries()].filter(([, value]) => value instanceof Blob);
        const total = entries.reduce((n, [, file]) => n + file.size, 0);
        if (!total) return this.parseCartResponse(await fetch(url, config));
        const leaveWarning = (event) => { event.preventDefault(); event.returnValue = ''; };
        window.addEventListener('beforeunload', leaveWarning);
        this.cupUploadSingle = true;
        this.cupUploadPercent = null;
        try {
          this.reportCupUpload(0);
          return await this.parseCartResponse(
            await this.postCart(url, formData, config.headers, entries, (bytes) => this.reportCupUpload(Math.min(1, bytes / total)))
          );
        } finally {
          window.removeEventListener('beforeunload', leaveWarning);
          this.cupUploadSingle = false;
        }
      }

      // Staged upload: Shopify rejects large cart requests (413), so the motif packages
      // travel in several small requests. The line stays marked with _UploadOffen until
      // the last package has arrived; checkout and production refuse such lines.
      // Phones: keep the screen awake during the upload, warn before large sets and ask the
      // customer to come back when the page is hidden. A 413 is retried once with small packages.
      async uploadCupSet(config, formData, files, withSections) {
        const sections = formData.getAll('sections');
        const sectionsUrl = formData.get('sections_url');
        formData.delete('sections');
        formData.delete('sections_url');
        const designs = this.form.cupSetPayload?.manifest?.designs?.length || 0;
        this.cupUploadNote = designs >= 20 ? this.cupLabel('uploadLargeHint') : '';
        let wakeLock = null;
        const keepAwake = async () => {
          try {
            if (navigator.wakeLock && document.visibilityState === 'visible' && (!wakeLock || wakeLock.released)) {
              wakeLock = await navigator.wakeLock.request('screen');
            }
          } catch {}
        };
        const title = document.title;
        const onVisibility = () => {
          const hint = this.cupLabel('uploadReturnHint');
          if (document.visibilityState === 'hidden') {
            if (hint) document.title = hint;
            this.cupUploadNote = hint || this.cupUploadNote;
          } else {
            document.title = title;
            keepAwake();
          }
          this.reportCupUpload(this.cupUploadFraction || 0, true);
        };
        document.addEventListener('visibilitychange', onVisibility);
        await keepAwake();
        try {
          try {
            return await this.uploadCupSetOnce(config, formData, files, withSections, sections, sectionsUrl, 10 * 1024 * 1024);
          } catch (error) {
            const repack = this.form.cupSetPayload?.repack;
            if (error.httpStatus !== 413 || !error.cartClean || !repack) throw error;
            const SMALL = 3 * 1024 * 1024;
            this.cupUploadNote = this.cupLabel('uploadRetrySmall') || this.cupUploadNote;
            this.reportCupUpload(0, true);
            const next = await repack(SMALL);
            for (const [key] of files) formData.delete(`properties[${key}]`);
            formData.set('properties[_CupSet]', JSON.stringify(next.manifest));
            return await this.uploadCupSetOnce(config, formData, next.files, withSections, sections, sectionsUrl, SMALL + 512 * 1024);
          }
        } finally {
          document.removeEventListener('visibilitychange', onVisibility);
          document.title = title;
          this.cupUploadNote = '';
          try { await wakeLock?.release(); } catch {}
        }
      }

      async uploadCupSetOnce(config, formData, files, withSections, sections, sectionsUrl, budget) {
        const batches = [];
        let batch = [], size = 0;
        for (const [key, file] of files) {
          if (batch.length && size + file.size > budget) { batches.push(batch); batch = []; size = 0; }
          batch.push([key, file]); size += file.size;
        }
        if (batch.length) batches.push(batch);
        const attach = (body, entries) => entries.forEach(([key, file]) => body.set(`properties[${key}]`, file, file.name));
        const addSections = (body) => {
          if (!withSections || !sectionsUrl) return;
          sections.forEach((value) => body.append('sections', value));
          body.append('sections_url', sectionsUrl);
        };
        formData.delete('properties[_cup_upload_token]');
        formData.delete('properties[_UploadOffen]');
        attach(formData, batches[0]);
        // progress over all packages; also shown when everything fits into a single request (Kani 18.10.2026)
        const total = files.reduce((n, [, file]) => n + file.size, 0);
        let sent = 0;
        this.cupUploadPercent = null;
        const progress = (bytes) => this.reportCupUpload(Math.min(1, (sent + bytes) / total));
        if (batches.length === 1) {
          addSections(formData);
          try {
            progress(0);
            return await this.parseCartResponse(await this.postCart(routes.cart_add_url, formData, config.headers, batches[0], progress));
          } catch (error) {
            // A rejected single request never created a cart line.
            if (error.httpStatus === 413) error.cartClean = true;
            throw error;
          } finally {
            formData.delete('sections');
            formData.delete('sections_url');
          }
        }

        const root = window.Shopify?.routes?.root || '/';
        const changeUrl = `${root}cart/change.js`;
        const token = randomId();
        const findLine = (items) => items?.find((item) => item.properties?._cup_upload_token === token);
        const arrived = (line, entries) => Boolean(line) && entries.every(([key]) =>
          typeof line.properties?.[key] === 'string' && line.properties[key].startsWith('https://cdn.shopify.com/'));
        const leaveWarning = (event) => { event.preventDefault(); event.returnValue = ''; };
        window.addEventListener('beforeunload', leaveWarning);
        formData.set('properties[_cup_upload_token]', token);
        formData.set('properties[_UploadOffen]', `1/${batches.length}`);
        let line = null;
        try {
          progress(0);
          const added = await this.parseCartResponse(
            await this.postCart(routes.cart_add_url, formData, config.headers, batches[0], progress)
          );
          if (added.status) return added;
          line = added.items ? findLine(added.items) : added;
          if (!arrived(line, batches[0]) || line.properties._cup_upload_token !== token) throw new Error(this.cartMessage('invalidResponse'));
          sent += batches[0].reduce((n, [, file]) => n + file.size, 0);

          let cart;
          for (let index = 1; index < batches.length; index++) {
            const entries = batches[index];
            const last = index === batches.length - 1;
            const makeBody = () => {
              const body = new FormData();
              body.set('id', line.key);
              body.set('quantity', String(line.quantity));
              for (const [key, value] of Object.entries(line.properties || {})) {
                if (key === '_UploadOffen' || value === null || value === undefined) continue;
                body.set(`properties[${key}]`, String(value));
              }
              if (!last) body.set('properties[_UploadOffen]', `${index + 1}/${batches.length}`);
              attach(body, entries);
              if (last) addSections(body);
              return body;
            };
            cart = await this.cartStep(changeUrl, makeBody, config.headers, entries, progress, async () => {
              const current = findLine((await this.readCartState()).items);
              if (!current) return null;
              if (arrived(current, entries) && (!last || !current.properties._UploadOffen)) return { done: true };
              line = current;
              return { done: false };
            });
            line = findLine(cart.items);
            if (!arrived(line, entries) || (last && line.properties._UploadOffen)) throw new Error(this.cartMessage('invalidResponse'));
            sent += entries.reduce((n, [, file]) => n + file.size, 0);
            progress(0);
          }
          if (withSections && sectionsUrl && !cart.sections) {
            // A retried last step returns the plain cart; fetch the drawer sections separately.
            try {
              const response = await fetch(`${sectionsUrl}?sections=${encodeURIComponent(sections.join(','))}`, { credentials: 'same-origin' });
              if (response.ok) cart = { ...cart, sections: await response.json() };
            } catch {}
          }
          return { ...cart, id: line.id };
        } catch (error) {
          let removed = false;
          try {
            const current = findLine((await this.readCartState()).items);
            if (current) {
              const response = await fetch(changeUrl, {
                method: 'POST', credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: current.key, quantity: 0 }),
              });
              removed = response.ok;
            } else removed = true;
          } catch {}
          if (removed) {
            // httpStatus/cartClean let uploadCupSet retry a 413 with smaller packages.
            throw Object.assign(new Error(this.cartMessage('uploadInterrupted')), { httpStatus: error.httpStatus, cartClean: true });
          }
          throw error;
        } finally {
          window.removeEventListener('beforeunload', leaveWarning);
        }
      }

      // One change request; transient failures (network, 429, 5xx) are retried after
      // checking whether Shopify already stored the package.
      async cartStep(url, makeBody, headers, entries, progress, recheck) {
        for (let attempt = 0; ; attempt++) {
          try {
            const cart = await this.parseCartResponse(await this.postCart(url, makeBody(), headers, entries, progress), true);
            if (cart.status) throw Object.assign(new Error(cart.description), { final: true });
            return cart;
          } catch (error) {
            const transient = error instanceof TypeError || error.httpStatus === 429 || error.httpStatus >= 500;
            if (error.final || !transient || attempt >= 3) throw error;
            await new Promise((resolve) => setTimeout(resolve, error.httpStatus === 429 ? 4000 * (attempt + 1) : 1500 * (attempt + 1)));
            const state = await recheck();
            if (!state) throw error;
            if (state.done) return this.readCartState();
          }
        }
      }

      // XHR instead of fetch: only XHR reports upload progress for the progress bar.
      postCart(url, body, headers = {}, entries = null, progress = null) {
        return new Promise((resolve, reject) => {
          const xhr = new XMLHttpRequest();
          xhr.open('POST', url);
          xhr.withCredentials = true;
          xhr.timeout = 180000;
          for (const [key, value] of Object.entries(headers)) if (key.toLowerCase() !== 'content-type') xhr.setRequestHeader(key, value);
          if (progress && entries) {
            const bytes = entries.reduce((n, [, file]) => n + file.size, 0);
            xhr.upload.onprogress = (event) => { if (event.lengthComputable && event.total) progress(bytes * event.loaded / event.total); };
          }
          xhr.onload = () => {
            const target = new URL(url, window.location.href).pathname;
            if (xhr.responseURL && new URL(xhr.responseURL).pathname !== target) return reject(new Error(this.cartMessage('invalidResponse')));
            try {
              resolve(new Response(xhr.responseText, { status: xhr.status, headers: { 'Content-Type': xhr.getResponseHeader('Content-Type') || 'text/plain' } }));
            } catch {
              reject(new TypeError('Network request failed'));
            }
          };
          xhr.onerror = xhr.ontimeout = xhr.onabort = () => reject(new TypeError('Network request failed'));
          xhr.send(body);
        });
      }

      async readCartState() {
        const root = window.Shopify?.routes?.root || '/';
        const cart = await this.parseCartResponse(await fetch(`${root}cart.js`, { credentials: 'same-origin', cache: 'no-store' }), true);
        if (cart.status) throw new Error(cart.description);
        return cart;
      }

      reportCupUpload(fraction, force = false) {
        const percent = Math.round(fraction * 100);
        if (percent === this.cupUploadPercent && !force) return;
        this.cupUploadPercent = percent;
        this.cupUploadFraction = fraction;
        // at 100 % the bytes are sent and Shopify still stores the files for a moment -> say so instead of standing at "100 %"
        const key = percent >= 100 ? 'uploadDone' : this.cupUploadSingle ? 'uploadProgressSingle' : 'uploadProgress';
        const fallback = { uploadDone: 'Fast fertig – wird im Warenkorb gespeichert …', uploadProgressSingle: 'Dein Motiv wird übertragen … {percent} %', uploadProgress: 'Deine Motive werden übertragen … {percent} %' }[key];
        const message = [(this.cupLabel(key) || fallback).replace('{percent}', String(percent)), this.cupUploadNote].filter(Boolean).join(' ');
        if (message) this.cart?.updateAddStatus?.(message);
        this.cart?.updateAddProgress?.(fraction);
        this.form.dispatchEvent(new CustomEvent('cup-set:upload-progress', { detail: { percent, message } }));
        // one motif: the set editor is not active, so the configurator shows the progress itself
        if (this.cupUploadSingle && message) document.querySelector('cup-3d-configurator')?.showStatus?.(message);
      }

      cupLabel(key) {
        const labels = document.querySelector('cup-3d-configurator [data-cup-set-labels]');
        try {
          const message = JSON.parse(labels?.textContent || '{}')[key];
          if (typeof message === 'string' && message.trim()) return message;
        } catch {}
        return '';
      }

      cartMessage(key) {
        return this.cupLabel(key) || window.cartStrings?.error || 'The cart response could not be confirmed. Please check your cart before trying again.';
      }

      async parseCartResponse(response, fullCart = false) {
        let data;
        const body = await response.text();
        try { data = JSON.parse(body); } catch {}
        const statusKey = response.status === 401 || response.status === 403 ? 'http401'
          : response.status === 413 ? 'http413'
          : response.status === 429 ? 'http429'
          : response.status >= 500 ? 'http500' : null;
        if (statusKey) {
          const error = new Error(this.cartMessage(statusKey));
          error.httpStatus = response.status;
          throw error;
        }
        if (!data || typeof data !== 'object' || Array.isArray(data) || response.redirected) {
          throw new Error(this.cartMessage('invalidResponse'));
        }
        if (!response.ok || data.status) {
          const message = typeof data.description === 'string' ? data.description
            : typeof data.message === 'string' ? data.message : this.cartMessage('invalidResponse');
          return { ...data, status: data.status || response.status || 422, description: message };
        }
        if (fullCart ? !Array.isArray(data.items) : !data.id && !Array.isArray(data.items)) {
          throw new Error(this.cartMessage('invalidResponse'));
        }
        return data;
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
